import React, { useMemo, useState } from 'react';
import {
  userTeam, teamById, userFixture, quoteFor, bidForPlayer, acceptOffer,
  rejectOffer, counterOffer, releasePlayer, listPlayer, unlistPlayer,
  configOf, economyOf, regularRoundsOf, signSponsor,
} from '../engine/career.js';
import { money, payrollOf } from '../engine/economy.js';
import { releaseCost } from '../engine/market.js';
import { sortedStandings, gamesBehind, leaders } from '../engine/season.js';
import { conferencesOf, playoffSize } from '../data/leagues.js';
import { dataDaRodada, porExtenso, rotuloTemporada } from '../engine/calendario.js';
import { POSITION_LABEL } from '../engine/players.js';
import { Ovr, Stat, Modal, Pill, BoxScore, derive } from './ui.jsx';
import { ClubBar } from './App.jsx';
import { DraftScreen } from './draft-screen.jsx';
import { Crest } from './crest.jsx';
import { JobBar, Unemployed } from './jobs-screen.jsx';
import { Flag, NATION_LABEL } from './flags.jsx';

// =========================================================== PAINEL
export function Painel({
  state, summary, busy, onAdvance, onNewSeason, onReset, onGo, onPlayLive, onBeginDraft, apply,
}) {
  const team = summary.team;
  const config = configOf(state);
  const fixture = userFixture(state);
  const opponentId = fixture && (fixture.home === state.clubId ? fixture.away : fixture.home);
  const opponent = opponentId ? teamById(state, opponentId) : null;
  const record = summary.record;
  const lastBox = state.boxscores[0];

  const activeSerie = state.bracket?.series.find(
    (s) => s.teams.includes(state.clubId) && !s.done,
  );

  if (state.unemployed) {
    return (
      <>
        <Unemployed state={state} apply={apply} busy={busy} onAdvance={onAdvance} />
        {state.phase === 'draft' && (
          <div className="card dim small">O draft acontece sem voce nesta temporada.</div>
        )}
        {state.phase === 'offseason' && (
          <button className="ghost" onClick={onBeginDraft}>avancar para o draft</button>
        )}
        <div style={{ marginTop: 20, marginBottom: 30 }}>
          <button className="danger sm" onClick={onReset}>apagar carreira</button>
        </div>
      </>
    );
  }

  return (
    <>
      <ClubBar state={state} />
      <JobBar state={state} />

      {/* Decisão pendente vem antes de tudo: é dinheiro parado, e no fim da
          tela ninguém rolava até lá. */}
      {(state.sponsorOffers || []).length > 0 && (
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <p className="kicker">• Decisão pendente</p>
          <div style={{ fontWeight: 800, fontSize: 16, margin: '2px 0 4px' }}>
            {state.sponsorOffers.length} propostas de patrocínio
          </div>
          <div className="small dim" style={{ marginBottom: 10 }}>
            Escolher uma é dinheiro certo por temporada. Enquanto você não
            assinar, o clube joga sem patrocinador.
          </div>
          <button className="primary" style={{ width: '100%' }}
            onClick={() => onGo('mercado')}>
            ver as propostas
          </button>
        </div>
      )}

      {state.phase === 'regular' && (
        <div className="card">
          <h3>
            Proximo jogo · rodada {state.round}
            {' · '}
            {porExtenso(dataDaRodada(state.season, state.round, regularRoundsOf(state)))}
          </h3>
          {opponent ? (
            <>
              <div className="row spread" style={{ marginBottom: 12 }}>
                <div className="row" style={{ gap: 10, minWidth: 0 }}>
                  <Crest team={opponent} size={38} />
                  <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{opponent.name}</div>
                  <div className="small muted">
                    {fixture.home === state.clubId ? 'Em casa' : 'Fora de casa'}
                    {' · '}
                    {state.standings[opponent.id].wins}-{state.standings[opponent.id].losses}
                  </div>
                  </div>
                </div>
                <Ovr value={Math.round(
                  [...opponent.roster].sort((a, b) => b.overall - a.overall)
                    .slice(0, 8).reduce((a, p) => a + p.overall, 0) / 8,
                )}
                />
              </div>
              {/* A acao principal ocupa a linha inteira; o resto e secundario
                  e fica embaixo, menor. Quatro botoes do mesmo tamanho soltos
                  em duas fileiras nao dizem qual e o caminho normal. */}
              <button className="primary play-btn" disabled={busy} onClick={onPlayLive}>
                {busy ? 'simulando...' : '▶  Jogar a partida'}
              </button>
              <div className="row" style={{ gap: 7, marginTop: 8 }}>
                <button className="ghost sm" style={{ flex: 1 }} disabled={busy}
                  onClick={() => onAdvance(1)}>simular</button>
                <button className="ghost sm" style={{ flex: 1 }} disabled={busy}
                  onClick={() => onAdvance(5)}>+5</button>
                <button className="ghost sm" style={{ flex: 1 }} disabled={busy}
                  onClick={() => onAdvance(Math.min(20, config.regularRounds - state.round + 1))}>
                  +{Math.min(20, config.regularRounds - state.round + 1)}
                </button>
              </div>
            </>
          ) : <div className="muted">Sem jogo nesta rodada.</div>}
        </div>
      )}

      {state.phase === 'playoffs' && (
        <div className="card">
          <h3>Playoffs · {config.roundNames[state.bracket.round]}</h3>
          {activeSerie ? (
            <>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                {activeSerie.names[0]} {activeSerie.wins[0]} — {activeSerie.wins[1]} {activeSerie.names[1]}
              </div>
              <div className="small muted" style={{ marginBottom: 12 }}>
                Melhor de {config.seriesWins[state.bracket.round] * 2 - 1} · jogo {activeSerie.games.length + 1}
              </div>
            </>
          ) : (
            <div className="muted" style={{ marginBottom: 12 }}>
              Voce nao esta mais nos playoffs. Os jogos seguem sem voce.
            </div>
          )}
          <button className="primary play-btn" disabled={busy}
            onClick={activeSerie ? onPlayLive : () => onAdvance(1)}>
            {busy ? 'simulando...' : activeSerie ? '▶  Jogar a partida' : 'Proximo jogo'}
          </button>
          <div className="row" style={{ gap: 7, marginTop: 8 }}>
            {activeSerie && (
              <button className="ghost sm" style={{ flex: 1 }} disabled={busy}
                onClick={() => onAdvance(1)}>simular</button>
            )}
            <button className="ghost sm" style={{ flex: 1 }} disabled={busy}
              onClick={() => onAdvance(7)}>avancar a fase</button>
          </div>
        </div>
      )}

      {state.phase === 'draft' && (
        <DraftScreen state={state} apply={apply} onFinish={onNewSeason} />
      )}

      {state.phase === 'offseason' && (
        <div className="card">
          <h3>Entressafra</h3>
          <div style={{ marginBottom: 12 }}>
            {state.bracket?.champion === state.clubId
              ? <span className="acc" style={{ fontWeight: 700 }}>Voce foi campeao da temporada {state.season}.</span>
              : <>Campeao: <b>{teamById(state, state.bracket?.champion)?.name || '-'}</b>.</>}
          </div>
          <div className="small muted" style={{ marginBottom: 12 }}>
            Agora vem o draft. Depois dele a proxima temporada comeca.
          </div>
          <button className="primary" onClick={onBeginDraft}>Ir para o draft</button>
        </div>
      )}

      <div className="grid auto">
        <Stat k="Campanha" v={`${record.wins}-${record.losses}`} />
        <Stat k={config.conferences ? 'Na conferencia' : 'Na liga'} v={`${summary.seed}º`}
          tone={summary.seed <= playoffSize(config) / conferencesOf(config).length ? 'pos' : 'warn'} />
        <Stat k="Sequencia" v={record.streak > 0 ? `${record.streak}V` : record.streak < 0 ? `${-record.streak}D` : '-'}
          tone={record.streak > 0 ? 'pos' : record.streak < 0 ? 'neg' : ''} />
        <Stat k="Pontos por jogo" v={record.games ? (record.pointsFor / record.games).toFixed(1) : '-'} />
      </div>

      {lastBox && (
        <>
          <h2>Ultimo jogo</h2>
          <div className="card">
            <div className="row spread wrap" style={{ gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div><b>{lastBox.away.name}</b> <span className="tabular">{lastBox.away.score}</span></div>
                <div><b>{lastBox.home.name}</b> <span className="tabular">{lastBox.home.score}</span></div>
              </div>
              <button className="ghost sm" onClick={() => onGo('jogos')}>ver sumula</button>
            </div>
          </div>
        </>
      )}

      {state.offers.length > 0 && (
        <>
          <h2>Propostas pelos seus atletas</h2>
          <div className="card">
            {state.offers.slice(0, 4).map((o) => (
              <div key={o.id} className="row spread" style={{ padding: '6px 0' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{o.playerName}</div>
                  <div className="small dim">
                    {o.buyerName} · {money(o.fee)}
                    {o.motivo ? ` · ${o.motivo}` : ''}
                  </div>
                </div>
                <span className="pill">até a {o.expiresRound}ª</span>
              </div>
            ))}
            <button className="primary" style={{ width: '100%', marginTop: 10 }}
              onClick={() => onGo('mercado')}>
              responder {state.offers.length === 1 ? 'a proposta' : `as ${state.offers.length} propostas`}
            </button>
          </div>
        </>
      )}


      {state.news.length > 0 && (
        <>
          <h2>Noticias do mercado</h2>
          <div className="card tight small">
            {state.news.slice(0, 6).map((n, i) => (
              <div key={i} style={{ padding: '6px 0', borderBottom: i < 5 ? '1px solid var(--line)' : 0 }}>
                {n.text}
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 22, marginBottom: 30 }}>
        <button className="danger sm" onClick={onReset}>apagar carreira</button>
      </div>
    </>
  );
}

// =========================================================== ELENCO
export function Elenco({ state, apply }) {
  const team = userTeam(state);
  const [sel, setSel] = useState(null);
  const [sort, setSort] = useState('overall');

  const stats = state.seasonStats;
  const roster = useMemo(() => {
    const list = [...team.roster];
    list.sort((a, b) => {
      if (sort === 'value') return b.marketValue - a.marketValue;
      if (sort === 'wage') return b.wage - a.wage;
      if (sort === 'age') return a.age - b.age;
      if (sort === 'pts') {
        const pa = stats[a.id] ? stats[a.id].pts / stats[a.id].games : -1;
        const pb = stats[b.id] ? stats[b.id].pts / stats[b.id].games : -1;
        return pb - pa;
      }
      return b.overall - a.overall;
    });
    return list;
  }, [team.roster, sort, state.round, state.season]);

  const player = sel && team.roster.find((p) => p.id === sel);
  const isListed = player && state.listed.includes(player.id);
  const line = player && stats[player.id];
  const d = line && derive(line, line.games);

  return (
    <>
      <ClubBar state={state} />
      <div className="chips c5" style={{ marginBottom: 10 }}>
        {[['overall', 'Overall'], ['pts', 'Pontos'], ['value', 'Valor'], ['wage', 'Salario'], ['age', 'Idade']].map(([id, label]) => (
          <button key={id} className={`sm ${sort === id ? 'primary' : 'ghost'}`} onClick={() => setSort(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th className="n">atleta</th><th>ovr</th><th>pot</th>
                <th className="col-stat">pts</th>
                <th className="col-stat">reb</th>
                <th className="col-stat">ast</th>
                <th>valor</th><th className="col-salario">salario</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((p) => {
                const l = stats[p.id];
                const dd = l && l.games ? derive(l, l.games) : null;
                return (
                  <tr key={p.id} className="click" onClick={() => setSel(p.id)}>
                    <td className="n">
                      {p.name}
                      {state.listed.includes(p.id) && <span className="acc"> ·</span>}
                      <span className="sub">{POSITION_LABEL[p.position]} · {p.age} anos</span>
                    </td>
                    <td><Ovr value={p.overall} /></td>
                    <td className="muted">{p.potential}</td>
                    <td className="col-stat">{dd ? dd.ptsPerGame.toFixed(1) : '-'}</td>
                    <td className="muted col-stat">{dd ? dd.rebPerGame.toFixed(1) : '-'}</td>
                    <td className="muted col-stat">{dd ? dd.astPerGame.toFixed(1) : '-'}</td>
                    <td>{money(p.marketValue)}</td>
                    <td className="muted col-salario">{money(p.wage)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {player && (
        <Modal title={player.name} onClose={() => setSel(null)}>
          <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
            <Pill>{POSITION_LABEL[player.position]}</Pill>
            <Pill>{player.age} anos</Pill>
            <span className="pill row" style={{ gap: 6 }}>
              <Flag code={player.nationality} size={14} title={NATION_LABEL[player.nationality]} />
              {NATION_LABEL[player.nationality] || player.nationality}
            </span>
            <Pill>contrato: {player.contractYears} ano(s)</Pill>
            {isListed && <Pill on>na lista de transferencias</Pill>}
          </div>

          <div className="grid auto" style={{ marginBottom: 14 }}>
            <Stat k="Overall" v={player.overall} />
            <Stat k="Potencial" v={player.potential} />
            <Stat k="Valor" v={money(player.marketValue)} />
            <Stat k="Salario" v={money(player.wage)} />
          </div>

          {d && (
            <>
              <h3>Temporada ({line.games} jogos)</h3>
              <div className="grid auto" style={{ marginBottom: 14 }}>
                <Stat k="Pontos" v={d.ptsPerGame.toFixed(1)} />
                <Stat k="Rebotes" v={d.rebPerGame.toFixed(1)} />
                <Stat k="Assistencias" v={d.astPerGame.toFixed(1)} />
                <Stat k="Minutos" v={d.minPerGame.toFixed(1)} />
                <Stat k="Quadra" v={`${(d.fgPct * 100).toFixed(1)}%`} />
                <Stat k="3 pontos" v={`${(d.threePct * 100).toFixed(1)}%`} />
              </div>
            </>
          )}

          <h3>Atributos</h3>
          <div className="grid two small" style={{ marginBottom: 6 }}>
            {[
              ['Arremesso de 2', 'twoShot'], ['Arremesso de 3', 'threeShot'],
              ['Lance livre', 'freeThrow'], ['Conducao', 'handle'],
              ['Passe', 'passing'], ['Defesa', 'defense'],
              ['Rebote def.', 'rebDef'], ['Rebote of.', 'rebOff'],
              ['Toco', 'block'], ['Roubo', 'steal'],
            ].map(([label, key]) => (
              <div key={key} className="row spread">
                <span className="muted">{label}</span>
                <b>{player.attributes[key]}</b>
              </div>
            ))}
          </div>

          <div className="row wrap" style={{ marginTop: 16, gap: 8 }}>
            {isListed ? (
              <button className="ghost" onClick={() => { apply((s) => unlistPlayer(s, player.id)); }}>
                tirar da lista
              </button>
            ) : (
              <button className="primary" onClick={() => { apply((s) => listPlayer(s, player.id)); }}>
                por na lista de transferencias
              </button>
            )}
            <button
              className="danger"
              onClick={() => {
                if (!window.confirm(`Dispensar ${player.name} custa ${money(releaseCost(player))}. Confirmar?`)) return;
                const r = apply((s) => releasePlayer(s, player.id));
                if (r?.events[0]?.kind === 'info') setSel(null);
              }}
            >
              dispensar ({money(releaseCost(player))})
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// =========================================================== MERCADO
export function Mercado({ state, apply }) {
  const [aba, setAba] = useState('propostas');
  const team = userTeam(state);
  const config = configOf(state);
  const marketOpen = state.phase === 'offseason'
    || (state.phase === 'regular' && state.round <= config.tradeDeadlineRound);

  return (
    <>
      <ClubBar state={state} />

      {!marketOpen && (
        <div className="card" style={{ borderLeft: '3px solid var(--warn)' }}>
          <b className="warn">Mercado fechado.</b>
          <div className="small muted" style={{ marginTop: 4 }}>
            {state.phase === 'playoffs'
              ? 'Nao ha negociacao durante os playoffs.'
              : `A data limite de trocas era a rodada ${config.tradeDeadlineRound}. Reabre na entressafra.`}
          </div>
        </div>
      )}

      <div className="chips" style={{ marginBottom: 12 }}>
        {[
          ['propostas', `Propostas (${state.offers.length})`],
          ['contratar', 'Contratar'],
          ['listados', `Minha lista (${state.listed.length})`],
          ['patrocinio', (state.sponsorOffers || []).length
            ? `Patrocínio (${state.sponsorOffers.length})`
            : 'Patrocínio'],
        ].map(([id, label]) => (
          <button key={id} className={`sm ${aba === id ? 'primary' : 'ghost'}`} onClick={() => setAba(id)}>
            {label}
          </button>
        ))}
      </div>

      {aba === 'propostas' && <Propostas state={state} apply={apply} />}
      {aba === 'contratar' && <Contratar state={state} apply={apply} marketOpen={marketOpen} />}
      {aba === 'listados' && <Listados state={state} apply={apply} team={team} />}
      {aba === 'patrocinio' && <Patrocinio state={state} apply={apply} />}
    </>
  );
}

function Propostas({ state, apply }) {
  const [counter, setCounter] = useState({});
  if (!state.offers.length) {
    return (
      <div className="card muted">
        Nenhuma proposta no momento. Ponha atletas na lista de transferencias para
        atrair interessados -- e nem todo atleta atrai: ninguem faz proposta pelo
        decimo terceiro homem.
      </div>
    );
  }
  return state.offers.map((offer) => {
    const player = userTeam(state).roster.find((p) => p.id === offer.playerId);
    if (!player) return null;
    const value = player.marketValue;
    const ratio = offer.fee / value;
    return (
      <div key={offer.id} className="card">
        <div className="row spread" style={{ marginBottom: 8 }}>
          <div>
            <b>{player.name}</b> <Ovr value={player.overall} />
            <div className="small muted">
              {POSITION_LABEL[player.position]} · {player.age} anos · vale {money(value)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 19, fontWeight: 700 }}>{money(offer.fee)}</div>
            <div className={`small ${ratio >= 1.1 ? 'pos' : ratio < 0.85 ? 'neg' : 'muted'}`}>
              {ratio >= 1.1 ? 'acima do valor' : ratio < 0.85 ? 'abaixo do valor' : 'no valor'}
            </div>
          </div>
        </div>
        <div className="small muted" style={{ marginBottom: 10 }}>
          {offer.buyerName} · salario oferecido ao atleta: {money(offer.wage)} ·
          {' '}vence na rodada {offer.expiresRound}
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="primary" onClick={() => apply((s) => acceptOffer(s, offer.id))}>aceitar</button>
          <button className="ghost" onClick={() => apply((s) => rejectOffer(s, offer.id))}>recusar</button>
          <input
            style={{ width: 110 }}
            type="number"
            placeholder={(offer.fee * 1.2).toFixed(1)}
            value={counter[offer.id] ?? ''}
            onChange={(e) => setCounter({ ...counter, [offer.id]: e.target.value })}
          />
          <button
            className="ghost"
            onClick={() => {
              const v = Number(counter[offer.id] || offer.fee * 1.2);
              apply((s) => counterOffer(s, offer.id, Math.round(v * 10) / 10));
            }}
          >
            contrapropor
          </button>
        </div>
      </div>
    );
  });
}

function Contratar({ state, apply, marketOpen }) {
  const [busca, setBusca] = useState('');
  const [pos, setPos] = useState('todas');
  const [maxPrice, setMaxPrice] = useState('');
  const [soCabe, setSoCabe] = useState(false);
  const [sel, setSel] = useState(null);

  const team = userTeam(state);
  const pool = useMemo(() => {
    const list = [];
    for (const t of state.teams) {
      if (t.id === state.clubId) continue;
      for (const p of t.roster) list.push({ player: p, team: t });
    }
    return list.sort((a, b) => b.player.overall - a.player.overall);
  }, [state.teams, state.round, state.season]);

  /* ⚠️ O clube PEDE mais do que o atleta vale -- por volta de 1,4x. Filtrar
     pelo valor de mercado mostrava gente que parecia caber no caixa e, na hora
     da proposta, devolvia "caixa insuficiente". No NBB, onde uma contratacao
     consome quase o caixa inteiro, isso acontecia quase sempre. */
  const PEDIDO = 1.4;
  const cabeNoCaixa = (player) => (player.marketValue ?? 0) * PEDIDO <= team.money;

  const filtered = pool.filter(({ player }) => {
    if (pos !== 'todas' && player.position !== pos) return false;
    if (maxPrice && player.marketValue > Number(maxPrice)) return false;
    if (soCabe && !cabeNoCaixa(player)) return false;
    if (busca && !player.name.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  }).slice(0, 60);

  const quantosCabem = pool.filter(({ player }) => cabeNoCaixa(player)).length;

  return (
    <>
      <div className="card">
        <input placeholder="buscar pelo nome" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <div className="row spread small" style={{ marginTop: 10 }}>
          <span className="dim">Seu caixa</span>
          <b>{money(team.money)} · {quantosCabem} atleta(s) ao seu alcance</b>
        </div>
        <button
          className={`sm ${soCabe ? 'primary' : 'ghost'}`}
          style={{ width: '100%', marginTop: 8 }}
          onClick={() => setSoCabe((v) => !v)}>
          {soCabe ? 'mostrando só quem cabe no caixa' : 'mostrar só quem cabe no caixa'}
        </button>
        <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>
          {['todas', 'ARM', 'AA', 'ALA', 'AP', 'PIV'].map((p) => (
            <button key={p} className={`sm ${pos === p ? 'primary' : 'ghost'}`} onClick={() => setPos(p)}>
              {p === 'todas' ? 'Todas' : p}
            </button>
          ))}
        </div>
        <div className="row" style={{ marginTop: 10, gap: 8 }}>
          <span className="small muted" style={{ whiteSpace: 'nowrap' }}>valor ate</span>
          <input type="number" placeholder="sem limite" value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)} />
          <span className="small muted">mi</span>
        </div>
      </div>

      <div className="card">
        <div className="scroll">
          <table>
            <thead>
              <tr><th className="n">atleta</th><th>ovr</th><th>idade</th><th>valor</th><th>clube</th></tr>
            </thead>
            <tbody>
              {filtered.map(({ player, team: owner }) => (
                <tr key={player.id} className="click" onClick={() => setSel(player.id)}>
                  <td className="n">{player.name}<span className="sub">{POSITION_LABEL[player.position]}</span></td>
                  <td><Ovr value={player.overall} /></td>
                  <td className="muted">{player.age}</td>
                  <td>{money(player.marketValue)}</td>
                  <td className="muted small">{owner.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!filtered.length && <div className="muted">Nenhum atleta com esses filtros.</div>}
      </div>

      {sel && (
        <Negociacao
          state={state} apply={apply} playerId={sel}
          onClose={() => setSel(null)} marketOpen={marketOpen} buyer={team}
        />
      )}
    </>
  );
}

/** A tela de proposta: mostra a cotacao ANTES de o usuario propor. */
function Negociacao({ state, apply, playerId, onClose, marketOpen, buyer }) {
  const econ = economyOf(state);
  const found = quoteFor(state, playerId);
  const [fee, setFee] = useState(() => (found ? String(found.quote.ask) : ''));
  const [wage, setWage] = useState(() => (found ? String(found.quote.wageWanted) : ''));
  const [last, setLast] = useState(null);
  if (!found) return null;

  const { player, seller } = found;
  // Recalcula a cotacao com o salario que o usuario esta oferecendo: salario
  // maior compra interesse, e o preco pedido cai junto.
  const q = quoteFor(state, playerId, Number(wage) || undefined).quote;
  const feeNum = Number(fee) || 0;
  const wageNum = Number(wage) || 0;
  const overCap = q.payrollAfter > econ.salaryCap;

  const propor = () => {
    const r = apply((s) => bidForPlayer(s, {
      playerId, fee: Math.round(feeNum * 10) / 10, wage: Math.round(wageNum * 10) / 10,
    }));
    const ev = r?.events?.[0];
    setLast(ev || null);
    if (ev?.kind === 'win') onClose();
    if (ev?.counterFee) setFee(String(ev.counterFee));
    if (ev?.counterWage) setWage(String(ev.counterWage));
  };

  return (
    <Modal title={player.name} onClose={onClose}>
      <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
        <Pill>{POSITION_LABEL[player.position]}</Pill>
        <Pill>{player.age} anos</Pill>
        <span className="pill row" style={{ gap: 6 }}>
          <Flag code={player.nationality} size={14} title={NATION_LABEL[player.nationality]} />
          {NATION_LABEL[player.nationality] || player.nationality}
        </span>
        <Pill>{seller.name}</Pill>
        <Pill on>{q.interestText}</Pill>
      </div>

      <div className="grid auto" style={{ marginBottom: 14 }}>
        <Stat k="Overall" v={player.overall} />
        <Stat k="Potencial" v={player.potential} />
        <Stat k="Valor de mercado" v={money(q.value)} />
        <Stat k="Preco pedido" v={money(q.ask)} tone="acc" />
      </div>

      <div className="card tight small" style={{ marginBottom: 14 }}>
        <div className="row spread"><span className="muted">Interesse do atleta</span><b>{q.interest}/100 · {q.interestText}</b></div>
        <div className="row spread"><span className="muted">Salario que ele quer</span><b>{money(q.wageWanted)}</b></div>
        <div className="row spread"><span className="muted">Folha depois do negocio</span>
          <b className={overCap ? 'warn' : 'pos'}>{money(q.payrollAfter)} / {money(econ.salaryCap)}</b>
        </div>
        <div className="row spread"><span className="muted">Elenco depois</span>
          <b className={q.rosterAfter > econ.maxRoster ? 'neg' : ''}>{q.rosterAfter}/{econ.maxRoster}</b>
        </div>
        <div className="row spread"><span className="muted">Seu caixa</span><b>{money(buyer.money)}</b></div>
      </div>

      <h3>Sua proposta</h3>
      <div className="row" style={{ gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>valor (mi)</div>
          <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>salario anual (mi)</div>
          <input type="number" value={wage} onChange={(e) => setWage(e.target.value)} />
        </div>
      </div>
      <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
        <button className="ghost sm" onClick={() => setFee(String(q.ask))}>pedir exato</button>
        <button className="ghost sm" onClick={() => setFee(String(Math.round(q.ask * 0.85 * 10) / 10))}>-15%</button>
        <button className="ghost sm" onClick={() => setWage(String(Math.round(q.wageWanted * 1.15 * 10) / 10))}>
          salario +15%
        </button>
      </div>

      {last && (
        <div className={`card tight small ${last.kind === 'error' ? 'neg' : last.kind === 'counter' ? 'warn' : 'pos'}`}
          style={{ marginBottom: 12 }}>
          {last.text}
        </div>
      )}

      <button
        className="primary"
        style={{ width: '100%', padding: 13 }}
        disabled={!marketOpen || feeNum <= 0 || wageNum <= 0}
        onClick={propor}
      >
        {marketOpen ? `propor ${money(feeNum)}` : 'mercado fechado'}
      </button>
    </Modal>
  );
}

function Listados({ state, apply, team }) {
  const listed = team.roster.filter((p) => state.listed.includes(p.id));
  if (!listed.length) {
    return (
      <div className="card muted">
        Sua lista esta vazia. Abra o elenco, toque num atleta e ponha na lista para
        que os outros clubes facam propostas.
      </div>
    );
  }
  return (
    <div className="card">
      <div className="scroll">
        <table>
          <thead><tr><th className="n">atleta</th><th>ovr</th><th>valor</th><th /></tr></thead>
          <tbody>
            {listed.map((p) => (
              <tr key={p.id}>
                <td className="n">{p.name}<span className="sub">{POSITION_LABEL[p.position]}</span></td>
                <td><Ovr value={p.overall} /></td>
                <td>{money(p.marketValue)}</td>
                <td><button className="ghost sm" onClick={() => apply((s) => unlistPlayer(s, p.id))}>tirar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========================================================== CLASSIFICACAO
export function Classificacao({ state }) {
  const config = configOf(state);
  const conferences = conferencesOf(config);
  const hasConferences = !!config.conferences;
  const [conf, setConf] = useState(conferences[0]);
  const [modo, setModo] = useState('tabela');
  const rows = sortedStandings(state.standings, hasConferences ? conf : null);
  const leader = rows[0];
  const cut = playoffSize(config) / conferences.length;

  return (
    <>
      <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
        <button className={`sm ${modo === 'tabela' ? 'primary' : 'ghost'}`} onClick={() => setModo('tabela')}>Classificacao</button>
        <button className={`sm ${modo === 'lideres' ? 'primary' : 'ghost'}`} onClick={() => setModo('lideres')}>Lideres</button>
      </div>

      {modo === 'tabela' ? (
        <>
          {hasConferences && (
            <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
              {conferences.map((c) => (
                <button key={c} className={`sm ${conf === c ? 'primary' : 'ghost'}`} onClick={() => setConf(c)}>{c}</button>
              ))}
            </div>
          )}
          <div className="card">
            <div className="scroll">
              <table>
                <thead>
                  <tr><th className="n">#</th><th className="n">time</th><th>v</th><th>d</th><th>%</th><th>jd</th><th>saldo</th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.teamId} className={r.teamId === state.clubId ? 'me' : ''}>
                      <td className="n muted">{i + 1}{i === cut - 1 && <span className="acc"> ·</span>}</td>
                      <td className="n">
                        <span className="row" style={{ gap: 7 }}>
                          <Crest team={teamById(state, r.teamId) || { name: r.name }} size={20} />
                          {r.name}
                        </span>
                      </td>
                      <td><b>{r.wins}</b></td>
                      <td className="muted">{r.losses}</td>
                      <td>{r.games ? (r.wins / r.games).toFixed(3).slice(1) : '-'}</td>
                      <td className="muted">{i === 0 ? '-' : gamesBehind(r, leader).toFixed(1)}</td>
                      <td className={r.pointsFor - r.pointsAgainst > 0 ? 'pos' : 'neg'}>
                        {r.games ? ((r.pointsFor - r.pointsAgainst) / r.games).toFixed(1) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="small muted" style={{ marginTop: 8 }}>
              {hasConferences
                ? `Os ${cut} primeiros de cada conferencia vao aos playoffs.`
                : `Os ${cut} primeiros vao aos playoffs.`}
            </div>
          </div>
        </>
      ) : <Lideres state={state} />}
    </>
  );
}

function Lideres({ state }) {
  const [campo, setCampo] = useState('pts');
  const lista = leaders(state.seasonStats, campo, 10, 12);
  return (
    <>
      <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
        {[['pts', 'Pontos'], ['tpm', 'Cestas de 3'], ['fgm2', 'Cestas de 2'], ['reb', 'Rebotes'],
          ['ast', 'Assistencias'], ['stl', 'Roubos'], ['blk', 'Tocos']].map(([id, label]) => (
          <button key={id} className={`sm ${campo === id ? 'primary' : 'ghost'}`} onClick={() => setCampo(id)}>{label}</button>
        ))}
      </div>
      <div className="card">
        <div className="scroll">
          <table>
            <thead><tr><th className="n">#</th><th className="n">atleta</th><th>por jogo</th><th>jogos</th></tr></thead>
            <tbody>
              {lista.map((p, i) => (
                <tr key={p.playerId} className={p.teamId === state.clubId ? 'me' : ''}>
                  <td className="n muted">{i + 1}</td>
                  <td className="n">{p.name}
                    <span className="sub">{teamById(state, p.teamId)?.name || ''}</span>
                  </td>
                  <td><b>{p.per.toFixed(1)}</b></td>
                  <td className="muted">{p.games}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!lista.length && <div className="muted">A temporada mal comecou. Volte depois de algumas rodadas.</div>}
      </div>
    </>
  );
}

// =========================================================== PLAYOFFS
export function Playoffs({ state }) {
  const config = configOf(state);
  const bracket = state.bracket;
  if (!bracket) return <Classificacao state={state} />;
  const rounds = Object.keys(config.roundNames).map(Number)
    .filter((r) => bracket.series.some((s) => s.round === r));

  return (
    <>
      {bracket.champion && (
        <div className="card" style={{ borderLeft: '3px solid var(--acc)' }}>
          <h3>Campeao da temporada {state.season}</h3>
          <div style={{ fontSize: 19, fontWeight: 700 }} className="acc">
            {teamById(state, bracket.champion)?.name}
          </div>
        </div>
      )}
      {rounds.map((r) => (
        <div key={r}>
          <h2>{config.roundNames[r]}</h2>
          <div className="bracket">
            {bracket.series.filter((s) => s.round === r).map((s) => (
              <div key={s.id} className="serie">
                {[0, 1].map((i) => (
                  <div key={i} className={`t ${s.done && s.winner === s.teams[i] ? 'w' : ''}`}>
                    <span className={s.teams[i] === state.clubId ? 'acc' : ''}>
                      {i === 0 ? `${s.highSeed}º ` : `${s.lowSeed}º `}{s.names[i]}
                    </span>
                    <span className="s tabular">{s.wins[i]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}


/**
 * PATROCÍNIO: o contrato em vigor, ou as propostas na mesa.
 *
 * A escolha tem peso porque não há rescisão: o contrato agressivo paga pouco
 * fixo e muito de bônus, e se a temporada não vier, o dinheiro não vem. Por
 * isso a tela mostra os dois números lado a lado, e não só o total possível --
 * um total possível esconde o risco.
 */
function Patrocinio({ state, apply }) {
  const sponsor = state.sponsor;
  const ofertas = state.sponsorOffers || [];

  if (sponsor) {
    const anos = sponsor.anosRestantes ?? sponsor.anos;
    return (
      <>
        <h2>Patrocínio</h2>
        <div className="card">
          <div className="row spread">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{sponsor.marca}</div>
              <div className="small dim">{sponsor.rotulo}</div>
            </div>
            <span className="pill on">{anos} {anos === 1 ? 'temporada' : 'temporadas'}</span>
          </div>
          <div className="grid two small" style={{ marginTop: 10 }}>
            <div className="row spread">
              <span className="dim">Fixo por temporada</span><b>{money(sponsor.fixo)}</b>
            </div>
            <div className="row spread">
              <span className="dim">Bônus</span><b className="pos">{money(sponsor.bonus)}</b>
            </div>
          </div>
          <div className="small" style={{ marginTop: 8 }}>
            O bônus sai se você <b>{sponsor.metaLabel}</b>.
          </div>
        </div>
      </>
    );
  }

  if (!ofertas.length) return null;

  return (
    <>
      <h2>Propostas de patrocínio</h2>
      <div className="card tight small dim" style={{ marginBottom: 8 }}>
        Escolha uma. O contrato vale pelo prazo inteiro e não tem rescisão.
      </div>
      <div className="grid" style={{ gap: 8 }}>
        {ofertas.map((o) => (
          <div key={o.id} className="card">
            <div className="row spread">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800 }}>{o.marca}</div>
                <div className="small dim">{o.rotulo} · {o.anos} temporadas</div>
              </div>
              <span className={`pill ${o.tipo === 'agressivo' ? 'gold' : ''}`}>{o.tipo}</span>
            </div>
            <div className="small" style={{ margin: '8px 0' }}>{o.resumo}</div>
            <div className="grid two small">
              <div className="row spread"><span className="dim">Fixo</span><b>{money(o.fixo)}</b></div>
              <div className="row spread"><span className="dim">Bônus</span><b className="pos">{money(o.bonus)}</b></div>
            </div>
            <div className="small dim" style={{ marginTop: 6 }}>
              O bônus exige <b>{o.metaLabel}</b>.
            </div>
            <button className="primary" style={{ width: '100%', marginTop: 10 }}
              onClick={() => apply((st) => signSponsor(st, o.id))}>
              assinar com {o.marca}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
